/**
 * Binary-CDO control extraction for the wizard Actions step.
 *
 * MODBUS, OPCUA, LOBBYVISION2 and SEQUENT adapters keep their control
 * commands ONLY in their protocol-specific binary CDO (no `keys` rows). Each
 * schema stores the LN2 message differently, but they all reduce to the same
 * composite key `"control:<variant>:<enumValue>"`:
 *
 *   - MODBUS   : template.writes[].msgId (already a composite key)
 *   - OPCUA    : nodeToMsg/msgToNode/methods/eventMappings[].msgId
 *   - LV2      : reads/writes[].message.message (FbMessageRef -> key)
 *   - SEQUENT  : discreteToMessage[].value.msg (MessageT -> key)
 *   - MCE      : the translation table's control rows that are not ignored
 *
 * This module decodes each and yields CdoControlEntry[] so the Actions
 * autogen + manual dropdown surface the adapter's real control writes. Only
 * this file knows the per-protocol shapes; StepActions calls
 * fetchBinaryCdoControlEntries(protocolKey, ...) and stays protocol-agnostic.
 */
import * as flatbuffers from "flatbuffers";
import { unwrapEnvelope } from "../../../flatbuffers/envelope";
import { Control } from "../../../flatbuffers/generated/control/control";
import { unpackModbusTemplate } from "../../../utils/modbusFlatBuffer";
import { decodeOpcUaMapping } from "../../../flatbuffers/builders/opcUaCdoBuilder";
import { decodeLobbyVision2Template } from "../../../flatbuffers/builders/lobbyVision2CdoBuilder";
import { Mapping as SequentMapping } from "../../../flatbuffers/generated/sequent/mapping";
import { fbRefToCompositeKey } from "../../components/adapters/ln2MappingConversions";
import { messageTToCompositeKey } from "../../../flatbuffers/types/valuedMessageBuilder";
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { controlEntriesOfMapping } from "../../../utils/mceIdl/controlEntries";

/** Control write mapping entry with per-lift / per-floor expansion metadata. */
export interface CdoControlEntry {
  enumValue: number;
  enumName: string;
  floorsBased: boolean;
  unitsBased: boolean;
}

const CONTROL_NAME_BY_VALUE = new Map<number, string>(
  Object.entries(Control as unknown as Record<string, string | number>)
    .filter(([, v]) => typeof v === "number")
    .map(([key, v]) => [v as number, key]),
);

/** Uppercase protocol keys whose control lives in a binary CDO. */
const BINARY_CDO_PROTOCOLS = new Set(["MODBUS", "OPCUA", "LOBBYVISION2", "SEQUENT", "MCE"]);

export function isBinaryCdoProtocol(protocolKey: string | undefined | null): boolean {
  return BINARY_CDO_PROTOCOLS.has((protocolKey ?? "").toUpperCase());
}

/**
 * Turn composite key strings into deduped control entries. Only a full
 * `control:<variant>:<enumValue>` key is a control command — anything shorter
 * (a register-offset entry, a status/traffic message) is skipped rather than
 * defaulted to enum 0 (which would fabricate a phantom CarCallLock).
 */
function entriesFromCompositeKeys(
  keys: (string | null | undefined)[],
  floorsBased: boolean,
  unitsBased: boolean,
): CdoControlEntry[] {
  const seen = new Set<number>();
  const entries: CdoControlEntry[] = [];
  keys.forEach((raw) => {
    const parts = (raw ?? "").split(":");
    if (parts.length < 3 || parts[0] !== "control") return;
    const enumVal = parseInt(parts[2], 10);
    if (Number.isNaN(enumVal) || seen.has(enumVal)) return;
    seen.add(enumVal);
    entries.push({
      enumValue: enumVal,
      enumName: CONTROL_NAME_BY_VALUE.get(enumVal) || `Control_${enumVal}`,
      floorsBased,
      unitsBased,
    });
  });
  return entries;
}

export function parseModbusControlEntries(bytes: Uint8Array): CdoControlEntry[] {
  const t = unpackModbusTemplate(bytes);
  const seen = new Set<number>();
  const entries: CdoControlEntry[] = [];
  (t.writes ?? []).forEach((w) => {
    const parts = (w.msgId ?? "").split(":");
    if (parts.length < 3 || parts[0] !== "control") return;
    const enumVal = parseInt(parts[2], 10);
    if (Number.isNaN(enumVal) || seen.has(enumVal)) return;
    seen.add(enumVal);
    entries.push({
      enumValue: enumVal,
      enumName: CONTROL_NAME_BY_VALUE.get(enumVal) || `Control_${enumVal}`,
      // Preserve the write's own expansion flags (MODBUS authored them).
      floorsBased: w.floorsBased ?? false,
      unitsBased: w.unitsBased ?? true,
    });
  });
  return entries;
}

export function parseOpcUaControlEntries(bytes: Uint8Array): CdoControlEntry[] {
  const o = decodeOpcUaMapping(bytes);
  const keys = [
    ...o.nodeToMsg.map((r) => r.msgId),
    ...o.msgToNode.map((r) => r.msgId),
    ...o.methods.map((r) => r.msgId),
    ...o.eventMappings.map((r) => r.msgId),
  ];
  return entriesFromCompositeKeys(keys, false, true);
}

export function parseLobbyVision2ControlEntries(bytes: Uint8Array): CdoControlEntry[] {
  const l = decodeLobbyVision2Template(bytes);
  const keys = [...l.reads, ...l.writes].map((r) => {
    const ref = r.message?.message;
    return ref ? fbRefToCompositeKey(ref) : null;
  });
  return entriesFromCompositeKeys(keys, false, true);
}

export function parseSequentControlEntries(bytes: Uint8Array): CdoControlEntry[] {
  const mapping = SequentMapping.getRootAsMapping(new flatbuffers.ByteBuffer(bytes)).unpack();
  const keys = (mapping.discreteToMessage ?? []).map((e) => {
    const msg = e?.value?.msg;
    return msg ? messageTToCompositeKey(msg) : null;
  });
  return entriesFromCompositeKeys(keys, false, true);
}

/**
 * Fetch and decode an adapter's binary CDO into its control entries, choosing
 * the parser by protocol. Non-binary-CDO protocols (LN1 etc.), a missing
 * adapter, or a failed fetch/decode all resolve to an empty array — the
 * decoders throw on a foreign payload, which is caught here.
 */
export async function fetchBinaryCdoControlEntries(
  protocolKey: string | undefined | null,
  adapterId: string,
  token: string,
): Promise<CdoControlEntry[]> {
  const key = (protocolKey ?? "").toUpperCase();
  if (!adapterId || !BINARY_CDO_PROTOCOLS.has(key)) return [];
  const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
  try {
    const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
    if (!bytes || bytes.length === 0) return [];
    switch (key) {
      case "MODBUS":
        return parseModbusControlEntries(bytes);
      case "OPCUA":
        return parseOpcUaControlEntries(bytes);
      case "LOBBYVISION2":
        return parseLobbyVision2ControlEntries(bytes);
      case "SEQUENT":
        return parseSequentControlEntries(bytes);
      case "MCE":
        return (await controlEntriesOfMapping(unwrapEnvelope(bytes))).map((entry) => ({
          ...entry,
          enumName: CONTROL_NAME_BY_VALUE.get(entry.enumValue) ?? "",
        }));
      default:
        return [];
    }
  } catch {
    return [];
  }
}
